import {
  clamp,
  resolveWidthPx,
  toCssLength,
  type PanelWidth,
} from './width-utils.js';

interface SizingConfig {
  panelMin: PanelWidth;
  panelMax: PanelWidth;
  contentMin: PanelWidth;
  handleWidth: number;
}

interface LayoutValues {
  panel: { width: number; minPx: number; maxPx: number };
  content: { width: number; widthCss: string; minPx: number; maxPx: number };
}

/** @internal */
export function clampPanelWidth(
  split: number,
  containerWidth: number,
  config: SizingConfig,
): number {
  const { panelMin, panelMax, contentMin, handleWidth } = config;

  const minPx = resolveWidthPx(panelMin, containerWidth, 0);
  const maxPx = resolveWidthPx(panelMax, containerWidth, Infinity);
  const contentMinPx = resolveWidthPx(contentMin, containerWidth, 0);

  let extrinsicMax = Infinity;
  if (containerWidth !== 0) {
    extrinsicMax = containerWidth - contentMinPx - handleWidth;
  }

  const finalMax = Math.max(minPx, Math.min(maxPx, extrinsicMax));
  return clamp(minPx, split, finalMax);
}

/** @internal */
export function computeLayout(
  splitPx: number,
  containerWidth: number,
  config: SizingConfig,
): LayoutValues {
  const { panelMin, panelMax, contentMin, handleWidth } = config;

  const panelWidth = clampPanelWidth(splitPx, containerWidth, config);

  const panelMinPx = resolveWidthPx(panelMin, containerWidth, 0);
  const panelMaxPx = resolveWidthPx(panelMax, containerWidth, Infinity);

  const contentMinPx = resolveWidthPx(contentMin, containerWidth, 0);
  const contentMaxPx = Math.max(0, containerWidth - contentMinPx - handleWidth);
  const contentWidth = clamp(
    contentMinPx,
    containerWidth - handleWidth - panelWidth,
    contentMaxPx,
  );

  const contentWidthCss = `max(${toCssLength(contentMin)}, 100% - ${
    panelWidth ? panelWidth + handleWidth : 0
  }px)`;

  return {
    panel: { width: panelWidth, minPx: panelMinPx, maxPx: panelMaxPx },
    content: {
      width: contentWidth,
      widthCss: contentWidthCss,
      minPx: contentMinPx,
      maxPx: contentMaxPx,
    },
  };
}
